import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api, { formatApiError } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { MagnifyingGlass, Trash, ShieldCheck, SpinnerGap, PencilSimple } from '@phosphor-icons/react';
import { useSaaSAuth } from '../contexts/SaaSAuthContext';
import { featureGate } from '../lib/entitlements';

export default function ContactsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useSaaSAuth();
  const mxGate = featureGate(user, 'has_bulk_verification');
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [verifying, setVerifying] = useState(new Set()); // contact IDs currently being verified
  const [editingContact, setEditingContact] = useState(null);
  const [salutationValue, setSalutationValue] = useState('');
  const [savingSalutation, setSavingSalutation] = useState(false);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, locale: (i18n.resolvedLanguage || i18n.language || 'de').split('-')[0] };
      if (search) params.search = search;
      const { data } = await api.get('/contacts', { params });
      setContacts(data.items);
      setTotal(data.total);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, search, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(t('workspace.contacts.confirmDelete', { count: selected.size }))) return;
    try {
      await api.post('/contacts/bulk-action', { contact_ids: [...selected], action: 'delete' });
      toast.success(t('workspace.contacts.deleted', { count: selected.size }));
      setSelected(new Set());
      fetchContacts();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const verifySingle = async (contactId) => {
    setVerifying(prev => new Set(prev).add(contactId));
    try {
      const { data } = await api.post(`/discovery/verify-mx-contact/${contactId}`);
      // Update the contact in-place without refetching everything
      setContacts(prev => prev.map(c =>
        c.id === contactId ? { ...c, email_status: data.status } : c
      ));
      const statusMsg = data.status === 'mx_confirmed' ? 'MX confirmed - email domain is valid' :
                        data.status === 'invalid' ? 'Invalid - domain does not exist' :
                        data.status === 'risky' ? 'Risky - MX lookup failed or timed out' : data.status;
      toast.success(`${data.email}: ${statusMsg}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setVerifying(prev => { const n = new Set(prev); n.delete(contactId); return n; }); }
  };

  const bulkVerify = async () => {
    if (!mxGate.allowed) {
      toast.error(mxGate.label);
      return;
    }
    const ids = selected.size > 0 ? [...selected] : contacts.map(c => c.id);
    const label = selected.size > 0 ? `${selected.size} selected` : `${ids.length} on this page`;
    if (!window.confirm(`Verify MX records for ${label} contacts? This checks if each email domain has real mail servers.`)) return;
    toast.info(`Verifying ${ids.length} contacts...`);
    setVerifying(new Set(ids));
    let confirmed = 0, invalid = 0, risky = 0;
    for (const cid of ids) {
      try {
        const { data } = await api.post(`/discovery/verify-mx-contact/${cid}`);
        setContacts(prev => prev.map(c =>
          c.id === cid ? { ...c, email_status: data.status } : c
        ));
        if (data.status === 'mx_confirmed') confirmed++;
        else if (data.status === 'invalid') invalid++;
        else risky++;
      } catch { risky++; }
      setVerifying(prev => { const n = new Set(prev); n.delete(cid); return n; });
    }
    toast.success(`Verification done: ${confirmed} confirmed, ${invalid} invalid, ${risky} risky`);
  };

  const emailTypeColor = (type) => {
    const map = {
      named_business: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      generic_business: 'bg-blue-50 text-blue-700 border-blue-200',
      role_based: 'bg-gray-100 text-gray-600 border-gray-200',
      personal_risky: 'bg-red-50 text-red-700 border-red-200',
      invalid: 'bg-red-100 text-red-800 border-red-300',
    };
    return map[type] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const emailStatusColor = (status) => {
    const map = {
      mx_confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      provider_verified: 'bg-green-50 text-green-800 border-green-300',
      syntactically_valid: 'bg-blue-50 text-blue-700 border-blue-200',
      unverified: 'bg-gray-100 text-gray-600 border-gray-200',
      risky: 'bg-amber-50 text-amber-700 border-amber-200',
      catch_all: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      invalid: 'bg-red-50 text-red-700 border-red-200',
    };
    return map[status] || 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const totalPages = Math.ceil(total / 50);

  const openSalutationEditor = (contact) => {
    setEditingContact(contact);
    setSalutationValue(contact.salutation_is_custom ? contact.salutation : '');
  };

  const saveSalutation = async () => {
    if (!editingContact) return;
    setSavingSalutation(true);
    try {
      await api.put(`/contacts/${editingContact.id}`, { salutation: salutationValue.trim() });
      toast.success(t('workspace.contacts.salutationSaved'));
      setEditingContact(null);
      await fetchContacts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSalutation(false);
    }
  };

  return (
    <div data-testid="contacts-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">{t('workspace.contacts.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('workspace.contacts.subtitle', { count: total })}</p>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder={t('workspace.contacts.search')} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 bg-white border-gray-300" data-testid="contacts-search" />
        </div>
        <Button
          variant="outline"
          onClick={bulkVerify}
          disabled={verifying.size > 0 || !mxGate.allowed}
          data-testid="verify-all-btn"
          title={mxGate.allowed ? 'Run MX DNS check on all contacts on this page to verify email domains are real' : mxGate.label}
        >
          <ShieldCheck size={16} className="mr-2" />
          {!mxGate.allowed
            ? mxGate.label
            : (verifying.size > 0
              ? t('workspace.contacts.verifying', { count: verifying.size })
              : (selected.size > 0 ? t('workspace.contacts.verifySelected', { count: selected.size }) : t('workspace.contacts.verifyAll')))}
        </Button>
      </div>

      <div className="border border-gray-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full min-w-[1180px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="w-10 py-3 px-4"><Checkbox checked={selected.size === contacts.length && contacts.length > 0} onCheckedChange={() => selected.size === contacts.length ? setSelected(new Set()) : setSelected(new Set(contacts.map(c => c.id)))} /></th>
              {['name', 'email', 'company', 'salutation', 'type', 'status', 'verify'].map((key) => <th key={key} className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3 text-left">{t(`workspace.contacts.${key}`)}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="py-12 text-center text-gray-400">{t('workspace.contacts.loading')}</td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan="8" className="py-12 text-center text-gray-400">{t('workspace.contacts.empty')}</td></tr>
            ) : contacts.map(c => (
              <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-3 px-4"><Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} /></td>
                <td className="py-2.5 px-3 text-sm text-gray-900 font-medium">{c.full_name || '-'}</td>
                <td className="py-2.5 px-3 text-sm font-mono text-gray-700">{c.email}</td>
                <td className="py-2.5 px-3 text-sm text-gray-600">{c.company_name || '-'}</td>
                <td className="py-2.5 px-3 text-sm text-gray-700 min-w-[260px]">
                  <button
                    type="button"
                    onClick={() => openSalutationEditor(c)}
                    className="group flex items-start gap-2 text-left hover:text-black"
                    title={t('workspace.contacts.editSalutation')}
                    data-testid={`edit-salutation-${c.id}`}
                  >
                    <span>{c.salutation}</span>
                    <PencilSimple size={14} className="mt-0.5 shrink-0 text-gray-400 group-hover:text-black" />
                  </button>
                  <span className="block text-[10px] uppercase tracking-wide text-gray-400 mt-1">
                    {c.salutation_is_custom ? t('workspace.contacts.customSalutation') : t('workspace.contacts.automaticSalutation')}
                  </span>
                </td>
                <td className="py-2.5 px-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${emailTypeColor(c.email_type)}`}>{c.email_type || 'unknown'}</span></td>
                <td className="py-2.5 px-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${emailStatusColor(c.email_status)}`}>{c.email_status || 'unverified'}</span></td>
                <td className="py-2.5 px-3">
                  {verifying.has(c.id) ? (
                    <SpinnerGap size={16} className="text-blue-500 animate-spin" />
                  ) : (
                    <button
                      onClick={() => verifySingle(c.id)}
                      className={`text-xs font-medium px-2 py-1 rounded border transition-colors ${
                        c.email_status === 'mx_confirmed' ? 'text-emerald-600 border-emerald-200 bg-emerald-50 cursor-default' :
                        c.email_status === 'invalid' ? 'text-red-600 border-red-200 bg-red-50 cursor-default' :
                        'text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100 cursor-pointer'
                      }`}
                      disabled={c.email_status === 'mx_confirmed' || c.email_status === 'provider_verified'}
                      data-testid={`verify-btn-${c.id}`}
                      title={c.email_status === 'mx_confirmed' ? 'Already verified' : 'Check MX records for this email domain'}
                    >
                      {c.email_status === 'mx_confirmed' ? t('workspace.contacts.verified') :
                       c.email_status === 'invalid' ? t('workspace.contacts.invalid') :
                       c.email_status === 'risky' ? t('workspace.contacts.recheck') : t('workspace.contacts.verify')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">{t('workspace.contacts.page', { page, total: totalPages })}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>{t('workspace.contacts.previous')}</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>{t('workspace.contacts.next')}</Button>
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-lg px-6 py-3 shadow-2xl flex items-center gap-4 z-50" data-testid="contacts-bulk-bar">
          <span className="text-sm font-mono">{t('workspace.contacts.selected', { count: selected.size })}</span>
          <div className="w-px h-6 bg-gray-600" />
          <button
            onClick={bulkVerify}
            disabled={!mxGate.allowed}
            className="flex items-center gap-1.5 text-sm hover:text-blue-300 transition-colors disabled:opacity-50"
            data-testid="bulk-verify-btn"
            title={mxGate.allowed ? undefined : mxGate.label}
          >
            <ShieldCheck size={16} /> {mxGate.allowed ? t('workspace.contacts.verifyMx') : mxGate.label}
          </button>
          <button onClick={bulkDelete} className="flex items-center gap-1.5 text-sm hover:text-red-300"><Trash size={16} /> {t('workspace.contacts.delete')}</button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-gray-400 hover:text-white">{t('workspace.contacts.clear')}</button>
        </div>
      )}

      <Dialog open={Boolean(editingContact)} onOpenChange={(open) => !open && setEditingContact(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{t('workspace.contacts.editSalutation')}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <p className="text-sm text-gray-500">{t('workspace.contacts.salutationHelp')}</p>
            <Input
              value={salutationValue}
              onChange={(event) => setSalutationValue(event.target.value)}
              placeholder={editingContact?.salutation || t('workspace.contacts.salutationPlaceholder')}
              data-testid="salutation-input"
            />
            <p className="text-xs text-gray-400">{t('workspace.contacts.salutationAutoHint')}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingContact(null)}>{t('workspace.contacts.cancel')}</Button>
              <Button onClick={saveSalutation} disabled={savingSalutation} data-testid="save-salutation-btn">
                {savingSalutation ? t('workspace.contacts.saving') : t('workspace.contacts.saveSalutation')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
